import { RootState, useAppSelector } from '../hooks';
import { createDeepEqualSelector } from '../selector.helpers';
import { store } from '../store';
import { GlobalQueryService } from './global.query.service';
import { GlobalQueryParams } from './queryParams.interface';
import {
  Pages,
  dispatchPageQueries,
  pageQueriesSelector,
} from './pageQueries.slice';

export class PageQuery<PageQuery extends { [key: string]: string | null }> {
  mergedQuerySelector!: (state: RootState) => PageQuery & GlobalQueryParams;
  protected dispatchPageQueries = dispatchPageQueries;
  protected pageQueriesSelector = pageQueriesSelector;

  constructor(
    protected globalQueryService: GlobalQueryService,
    protected pageName: Pages,
  ) {
    this.createMergeQueryParamsSelector();
  }

  useSelectMergedQuery = () => useAppSelector(this.mergedQuerySelector);

  // set property to null to remove it
  dispatchQueryParams = (
    queryParams: Partial<PageQuery & GlobalQueryParams>,
  ) => {
    this.dispatchValidPageQuery(queryParams as Partial<PageQuery>);
    this.globalQueryService.dispatchQueryParams(queryParams);
  };

  // TIP: override this method in your page instance to declare what queryParams are valid for this page.
  protected getValidPageQueryParams = (
    queryParams: Partial<PageQuery>,
  ): PageQuery => {
    return {} as PageQuery;
  };

  private createMergeQueryParamsSelector = () => {
    this.mergedQuerySelector = createDeepEqualSelector(
      this.pageQueriesSelector,
      this.globalQueryService.globalQuerySelector,
      (
        pageQueries,
        globalQuery: GlobalQueryParams,
      ): PageQuery & GlobalQueryParams => {
        const pageQuery = pageQueries[this.pageName];
        return {
          ...pageQuery,
          ...globalQuery,
        };
      },
    );
  };

  private dispatchValidPageQuery(queryParams: Partial<PageQuery>) {
    const validQueryParams = this.getValidPageQueryParams(queryParams);
    const usedQueryParams = this.removeUnusedQueryParams(validQueryParams);
    store.dispatch(
      this.dispatchPageQueries({
        query: usedQueryParams,
        pageName: this.pageName,
      }),
    );
  }

  // removes unused properties so they don't overwrite storeParams in useQuerySync hook.
  private removeUnusedQueryParams = (
    validQueryParams: PageQuery,
  ): Partial<PageQuery> => {
    const usedQueryParams = {
      ...validQueryParams,
    };
    Object.keys(usedQueryParams).forEach(
      (key) =>
        usedQueryParams[key as keyof PageQuery] === undefined &&
        delete usedQueryParams[key as keyof PageQuery],
    );
    return usedQueryParams;
  };
}
